<template>
  <div class="process-preview-container">
    <!-- 流程基本信息 -->
    <div class="preview-header">
      <div class="process-info">
        <h3>{{ processData.definition_name }}</h3>
        <div class="info-tags">
          <el-tag :type="processData.is_active ? 'success' : 'info'" size="small">
            {{ processData.is_active ? '已激活' : '未激活' }}
          </el-tag>
          <el-tag v-if="processData.is_default" type="success" size="small">默认流程</el-tag>
          <!-- <el-tag size="small">版本: v{{ processData.version }}</el-tag> -->
        </div>
        <p class="description" v-if="processData.description">{{ processData.description }}</p>
      </div>

      <div class="preview-tools">
        <el-button size="small" icon="el-icon-zoom-in" @click="handleZoomIn">放大</el-button>
        <el-button size="small" icon="el-icon-zoom-out" @click="handleZoomOut">缩小</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>
    </div>

    <!-- 流程统计 -->
    <div class="preview-stats">
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-value">{{ nodes.length }}</div>
          <div class="stat-label">总节点数</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ startNodes.length }}</div>
          <div class="stat-label">开始节点</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ endNodes.length }}</div>
          <div class="stat-label">结束节点</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ approvalNodes.length }}</div>
          <div class="stat-label">审批节点</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ paymentNodes.length }}</div>
          <div class="stat-label">付款节点</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ executionNodes.length }}</div>
          <div class="stat-label">执行节点</div>
        </div>
      </div>
    </div>

    <!-- 画布区域 -->
    <div class="preview-canvas-container">
      <div
        class="preview-canvas"
        ref="canvasRef"
        :style="{ transform: `scale(${zoomLevel})`, transformOrigin: 'center center' }"
      >
        <!-- 网格背景 -->
        <div class="canvas-grid"></div>

        <!-- 节点容器 -->
        <div class="nodes-container">
          <div
            v-for="node in sortedNodes"
            :key="node.id"
            class="preview-node"
            :style="{
              left: `${node.position_x}px`,
              top: `${node.position_y}px`,
              width: '200px'
            }"
            :class="[
              `node-type-${node.node_type}`,
              {
                'node-start': node.node_type === 1,
                'node-end': node.node_type === 6,
                'node-highlight': highlightedNode === node.id,
                'node-current': currentInstanceNodeId === node.id
              }
            ]"
            @mouseenter="handleNodeHover(node)"
            @mouseleave="handleNodeLeave"
            @click="handleNodeClick(node)"
          >
            <!-- 节点头部 -->
            <div class="node-header">
              <div class="node-icon">
                <i :class="getNodeIcon(node.node_type)"></i>
              </div>
              <div class="node-title">
                <div class="node-name">{{ node.node_name }}</div>
                <div class="node-no">{{ node.node_no }}</div>
              </div>
            </div>

            <!-- 节点内容 -->
            <div class="node-content">
              <!-- 处理人信息 -->
              <div class="node-handler" v-if="node.handler_type !== 3">
                <span class="handler-label">处理人：</span>
                <span class="handler-value">{{ getHandlerInfo(node) }}</span>
              </div>

              <!-- 特殊要求 -->
              <div class="node-requirements" v-if="hasRequirements(node)">
                <div class="requirement-item" v-if="node.require_signature">
                  <i class="el-icon-edit"></i>需要签名
                </div>
                <div class="requirement-item" v-if="node.require_photos">
                  <i class="el-icon-picture"></i>需要照片
                </div>
                <div class="requirement-item" v-if="node.require_attachments">
                  <i class="el-icon-paperclip"></i>需要附件
                </div>
                <div class="requirement-item" v-if="node.require_payment">
                  <i class="el-icon-money"></i>
                  {{ getPaymentTypeText(node.payment_type) }}
                  <span v-if="node.payment_percentage">({{ node.payment_percentage }}%)</span>
                </div>
                <div class="requirement-item" v-if="node.require_quotation">
                  <i class="el-icon-document"></i>需要报价
                </div>
                <div class="requirement-item" v-if="node.require_warranty_check">
                  <i class="el-icon-check"></i>质保检查
                </div>
              </div>

              <!-- 时间限制 -->
              <div class="node-time" v-if="node.time_limit_hours">
                <i class="el-icon-time"></i>时限：{{ node.time_limit_hours }}小时
              </div>
            </div>

            <!-- 节点底部 -->
            <div class="node-footer">
              <div class="node-status">
                <span class="node-type-text">{{ getNodeTypeText(node.node_type) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 连线容器 -->
        <svg class="connections-container" ref="connectionsRef">
          <defs>
            <!-- 箭头定义 -->
            <marker
              id="arrowhead-normal"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#409eff" />
            </marker>
            <marker
              id="arrowhead-highlight"
              markerWidth="12"
              markerHeight="9"
              refX="10"
              refY="4.5"
              orient="auto"
            >
              <polygon points="0 0, 12 4.5, 0 9" fill="#f56c6c" />
            </marker>
            <!-- 条件分支连线样式 -->
            <marker
              id="arrowhead-condition"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#67c23a" />
            </marker>
            <!-- 虚线箭头 -->
            <marker
              id="arrowhead-dashed"
              markerWidth="10"
              markerHeight="7"
              refX="9"
              refY="3.5"
              orient="auto"
            >
              <polygon points="0 0, 10 3.5, 0 7" fill="#909399" />
            </marker>
          </defs>

          <!-- 常规连线 -->
          <path
            v-for="(conn, index) in connections"
            :key="`conn-${index}`"
            :d="conn.path"
            class="connection-line"
            :class="{
              'line-highlight': highlightedConnection === index,
              'line-dashed': conn.dashed,
              'line-condition': conn.condition
            }"
            :marker-end="conn.condition ? 'url(#arrowhead-condition)' : conn.dashed ? 'url(#arrowhead-dashed)' : 'url(#arrowhead-normal)'"
          />

          <!-- 并行分支 -->
          <path
            v-for="(parallel, index) in parallelConnections"
            :key="`parallel-${index}`"
            :d="parallel.path"
            class="connection-line line-parallel"
            marker-end="url(#arrowhead-normal)"
          />

          <!-- 分支条件文本 -->
          <g v-for="(branch, index) in branchLabels" :key="`branch-${index}`">
            <rect
              :x="branch.x - 40"
              :y="branch.y - 15"
              width="80"
              height="30"
              rx="4"
              fill="#f0f9ff"
              stroke="#d9ecff"
              stroke-width="1"
            />
            <text
              :x="branch.x"
              :y="branch.y + 5"
              text-anchor="middle"
              fill="#409eff"
              font-size="12"
            >
              {{ branch.label }}
            </text>
          </g>
        </svg>
      </div>

      <!-- 缩放控制 -->
      <div class="zoom-controls">
        <el-slider
          v-model="zoomPercentage"
          vertical
          height="150px"
          :min="30"
          :max="200"
          @input="handleZoomSlider"
        />
      </div>
    </div>

    <!-- 节点详情侧边栏 -->
    <div class="preview-sidebar" v-if="selectedNode">
      <div class="sidebar-header">
        <h4>节点详情</h4>
        <el-button icon="el-icon-close" size="small" circle @click="selectedNode = null" />
      </div>

      <div class="node-detail">
        <div class="detail-section">
          <h5>基本信息</h5>
          <div class="detail-row">
            <span class="label">节点编号：</span>
            <span class="value">{{ selectedNode.node_no }}</span>
          </div>
          <div class="detail-row">
            <span class="label">节点名称：</span>
            <span class="value">{{ selectedNode.node_name }}</span>
          </div>
          <div class="detail-row">
            <span class="label">节点类型：</span>
            <span class="value">{{ getNodeTypeText(selectedNode.node_type) }}</span>
          </div>
          <div class="detail-row">
            <span class="label">操作类型：</span>
            <span class="value">{{ getActionTypeText(selectedNode.action_type) }}</span>
          </div>
        </div>

        <div class="detail-section">
          <h5>处理人配置</h5>
          <div class="detail-row">
            <span class="label">处理人类型：</span>
            <span class="value">{{ getHandlerTypeText(selectedNode.handler_type) }}</span>
          </div>

          <template v-if="selectedNode.handler_type === 1 && selectedNode.handler_role_ids">
            <div class="detail-row">
              <span class="label">处理角色：</span>
              <span class="value">
                {{ getRoleNames(selectedNode.handler_role_ids) }}
              </span>
            </div>
          </template>

          <template v-if="selectedNode.handler_type === 2 && selectedNode.handler_user_ids">
            <div class="detail-row">
              <span class="label">处理人员：</span>
              <span class="value">
                {{ getUserNames(selectedNode.handler_user_ids) }}
              </span>
            </div>
          </template>
        </div>

        <div class="detail-section" v-if="hasRequirements(selectedNode)">
          <h5>特殊要求</h5>
          <div class="requirements-list">
            <div class="requirement-item" v-if="selectedNode.require_signature">
              <i class="el-icon-edit"></i>需要签名
            </div>
            <div class="requirement-item" v-if="selectedNode.require_photos">
              <i class="el-icon-picture"></i>需要照片
            </div>
            <div class="requirement-item" v-if="selectedNode.require_attachments">
              <i class="el-icon-paperclip"></i>需要附件
            </div>
            <div class="requirement-item" v-if="selectedNode.require_payment">
              <i class="el-icon-money"></i>
              {{ getPaymentTypeText(selectedNode.payment_type) }}
              <span v-if="selectedNode.payment_percentage">({{ selectedNode.payment_percentage }}%)</span>
              <span v-if="selectedNode.payment_amount"> 金额：{{ selectedNode.payment_amount }}元</span>
            </div>
            <div class="requirement-item" v-if="selectedNode.require_quotation">
              <i class="el-icon-document"></i>需要报价
            </div>
            <div class="requirement-item" v-if="selectedNode.require_warranty_check">
              <i class="el-icon-check"></i>需要质保检查
            </div>
          </div>
        </div>

        <div class="detail-section" v-if="selectedNode.time_limit_hours">
          <h5>时间限制</h5>
          <div class="detail-row">
            <span class="label">时限：</span>
            <span class="value">{{ selectedNode.time_limit_hours }}小时</span>
          </div>
          <div class="detail-row">
            <span class="label">超时处理：</span>
            <span class="value">{{ getTimeoutActionText(processData.timeout_action) }}</span>
          </div>
        </div>

        <div class="detail-section" v-if="selectedNode.allow_actions">
          <h5>允许的操作</h5>
          <div class="actions-list">
            <el-tag
              v-for="action in getAllowedActions(selectedNode.allow_actions)"
              :key="action"
              size="small"
              type="info"
              class="action-tag"
            >
              {{ action }}
            </el-tag>
          </div>
        </div>

        <div class="detail-section" v-if="selectedNode.next_node_id">
          <h5>下一节点</h5>
          <div class="detail-row">
            <span class="label">下一节点：</span>
            <span class="value">{{ getNextNodeName(selectedNode.next_node_id) }}</span>
          </div>
        </div>

        <div class="detail-section" v-if="selectedNode.branch_conditions">
          <h5>分支条件</h5>
          <div class="branch-conditions">
            <pre>{{ formatBranchConditions(selectedNode.branch_conditions) }}</pre>
          </div>
        </div>

        <div class="detail-section" v-if="selectedNode.remark">
          <h5>备注</h5>
          <div class="remark-content">{{ selectedNode.remark }}</div>
        </div>
      </div>
    </div>

    <!-- 图例 -->
    <div class="preview-legend">
      <div class="legend-title">图例说明</div>
      <div class="legend-items">
        <div class="legend-item">
          <div class="legend-color node-type-1"></div>
          <span>开始节点</span>
        </div>
        <div class="legend-item">
          <div class="legend-color node-type-2"></div>
          <span>审批节点</span>
        </div>
        <div class="legend-item">
          <div class="legend-color node-type-3"></div>
          <span>执行节点</span>
        </div>
        <div class="legend-item">
          <div class="legend-color node-type-4"></div>
          <span>验收节点</span>
        </div>
        <div class="legend-item">
          <div class="legend-color node-type-5"></div>
          <span>付款节点</span>
        </div>
        <div class="legend-item">
          <div class="legend-color node-type-6"></div>
          <span>结束节点</span>
        </div>
        <!-- <div class="legend-item">
          <div class="legend-color node-type-7"></div>
          <span>条件节点</span>
        </div> -->
        <!-- <div class="legend-item">
          <div class="legend-color node-type-8"></div>
          <span>并行节点</span>
        </div> -->
        <!-- <div class="legend-item">
          <div class="legend-color node-current"></div>
          <span>当前实例节点</span>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
// import html2canvas from 'html2canvas'

export default {
  name: 'ProcessPreview',

  props: {
    processData: {
      type: Object,
      required: true
    },
    nodes: {
      type: Array,
      required: true
    },
    currentInstanceNodeId: {
      type: Number,
      default: null
    }
  },

  data () {
    return {
      zoomLevel: 0.8,
      zoomPercentage: 80,
      selectedNode: null,
      highlightedNode: null,
      highlightedConnection: null,
      connections: [],
      parallelConnections: [],
      branchLabels: [],

      nodeTypes: {
        1: { name: '开始节点', color: '#67c23a' },
        2: { name: '审批节点', color: '#409eff' },
        3: { name: '执行节点', color: '#e6a23c' },
        4: { name: '验收节点', color: '#f56c6c' },
        5: { name: '付款节点', color: '#9b8bba' },
        6: { name: '结束节点', color: '#909399' },
        7: { name: '条件节点', color: '#8cc269' },
        8: { name: '并行节点', color: '#f78989' }
      },

      handlerTypes: {
        1: '指定角色',
        2: '指定人员',
        3: '发起人',
        4: '上级领导',
        5: '项目经理',
        6: '客户',
        7: '供应商',
        8: '动态指定'
      },

      actionTypes: {
        1: '审批',
        2: '执行',
        3: '验收',
        4: '付款',
        5: '报价',
        6: '派单',
        7: '采购',
        8: '入库'
      },

      approvalTypes: {
        1: '串签',
        2: '并签',
        3: '或签'
      },

      paymentTypes: {
        1: '定金',
        2: '进度款',
        3: '尾款',
        4: '全款'
      },

      timeoutActions: {
        1: '自动通过',
        2: '自动驳回',
        3: '转交他人',
        4: '提醒'
      }
    }
  },

  computed: {
    sortedNodes () {
      // 按sort字段排序
      return [...this.nodes].sort((a, b) => a.sort - b.sort)
    },

    startNodes () {
      return this.nodes.filter(node => node.node_type === 1)
    },

    endNodes () {
      return this.nodes.filter(node => node.node_type === 6)
    },

    approvalNodes () {
      return this.nodes.filter(node => node.node_type === 2)
    },

    paymentNodes () {
      return this.nodes.filter(node => node.node_type === 5)
    },

    executionNodes () {
      return this.nodes.filter(node => node.node_type === 3)
    }
  },

  watch: {
    nodes: {
      immediate: true,
      handler () {
        this.$nextTick(() => {
          this.generateConnections()
        })
      }
    },

    currentInstanceNodeId () {
      if (this.currentInstanceNodeId) {
        const node = this.nodes.find(n => n.id === this.currentInstanceNodeId)
        if (node) {
          this.selectedNode = node
        }
      }
    }
  },

  mounted () {
    this.generateConnections()
    this.centerView()
    // 移除自动布局，保留设计页面设置的位置
    // this.autoLayoutNodes()
  },

  methods: {
    // 生成连线
    generateConnections () {
      this.connections = []
      this.parallelConnections = []
      this.branchLabels = []

      this.nodes.forEach(node => {
        // 常规连线
        if (node.next_node_id) {
          const targetNode = this.nodes.find(n => n.id === node.next_node_id)
          if (targetNode) {
            const path = this.calculatePath(node, targetNode)
            this.connections.push({
              from: node.id,
              to: targetNode.id,
              path,
              dashed: node.node_type === 7, // 条件节点使用虚线
              condition: node.node_type === 7
            })

            // 条件分支标签
            if (node.node_type === 7 && node.branch_conditions) {
              const midpoint = this.calculateMidpoint(node, targetNode)
              try {
                const conditions = JSON.parse(node.branch_conditions)
                const condition = conditions.find(c => c.node_id === targetNode.id)
                if (condition) {
                  this.branchLabels.push({
                    x: midpoint.x,
                    y: midpoint.y,
                    label: condition.label || '条件'
                  })
                }
              } catch (e) {
                console.error('解析分支条件失败:', e)
              }
            }
          }
        }

        // 并行节点连线
        if (node.node_type === 8 && node.parallel_nodes) {
          try {
            const parallelNodeIds = JSON.parse(node.parallel_nodes)
            parallelNodeIds.forEach(parallelNodeId => {
              const targetNode = this.nodes.find(n => n.id === parallelNodeId)
              if (targetNode) {
                const path = this.calculateParallelPath(node, targetNode, parallelNodeIds.indexOf(parallelNodeId))
                this.parallelConnections.push({
                  from: node.id,
                  to: targetNode.id,
                  path
                })
              }
            })
          } catch (e) {
            console.error('解析并行节点失败:', e)
          }
        }
      })
    },

    // 计算常规连线路径
    calculatePath (fromNode, toNode) {
      const fromRect = this.getNodeRect(fromNode)
      const toRect = this.getNodeRect(toNode)

      if (!fromRect || !toRect) return ''

      // 起点：源节点右侧中点
      const startX = fromRect.left + fromRect.width
      const startY = fromRect.top + fromRect.height / 2

      // 终点：目标节点左侧中点
      const endX = toRect.left
      const endY = toRect.top + toRect.height / 2

      // 计算控制点，形成平滑曲线
      const controlX1 = startX + Math.abs(endX - startX) / 3
      const controlY1 = startY
      const controlX2 = endX - Math.abs(endX - startX) / 3
      const controlY2 = endY

      return `M ${startX} ${startY} C ${controlX1} ${controlY1}, ${controlX2} ${controlY2}, ${endX} ${endY}`
    },

    // 计算并行连线路径
    calculateParallelPath (fromNode, toNode, index) {
      const fromRect = this.getNodeRect(fromNode)
      const toRect = this.getNodeRect(toNode)

      if (!fromRect || !toRect) return ''

      // 起点：源节点右侧，根据索引偏移
      const startX = fromRect.left + fromRect.width
      const startY = fromRect.top + (fromRect.height / 4) * (index + 1)

      // 终点：目标节点左侧，根据索引偏移
      const endX = toRect.left
      const endY = toRect.top + (toRect.height / 4) * (index + 1)

      // 垂直线段连接
      return `M ${startX} ${startY} L ${endX} ${endY}`
    },

    // 计算中点（用于分支标签）
    calculateMidpoint (fromNode, toNode) {
      const fromRect = this.getNodeRect(fromNode)
      const toRect = this.getNodeRect(toNode)

      if (!fromRect || !toRect) return { x: 0, y: 0 }

      const midX = (fromRect.left + fromRect.width + toRect.left) / 2
      const midY = (fromRect.top + fromRect.height / 2 + toRect.top + toRect.height / 2) / 2

      return { x: midX, y: midY }
    },

    // 获取节点位置和尺寸
    getNodeRect (node) {
      const element = this.$refs.canvasRef?.querySelector(`.preview-node[data-id="${node.id}"]`)
      if (element) {
        return element.getBoundingClientRect()
      }
      return null
    },

    // 获取节点图标
    getNodeIcon (nodeType) {
      const iconMap = {
        1: 'el-icon-caret-right',
        2: 'el-icon-s-check',
        3: 'el-icon-s-operation',
        4: 'el-icon-circle-check',
        5: 'el-icon-money',
        6: 'el-icon-switch-button',
        7: 'el-icon-connection',
        8: 'el-icon-copy-document'
      }
      return iconMap[nodeType] || 'el-icon-question'
    },

    // 获取节点类型文本
    getNodeTypeText (nodeType) {
      return this.nodeTypes[nodeType]?.name || '未知节点'
    },

    // 获取审批类型文本
    getApprovalTypeText (type) {
      return this.approvalTypes[type] || '未知'
    },

    // 获取处理人类型文本
    getHandlerTypeText (type) {
      return this.handlerTypes[type] || '未知'
    },

    // 获取操作类型文本
    getActionTypeText (type) {
      return this.actionTypes[type] || '未知'
    },

    // 获取付款类型文本
    getPaymentTypeText (type) {
      return this.paymentTypes[type] || '未知'
    },

    // 获取超时处理文本
    getTimeoutActionText (type) {
      return this.timeoutActions[type] || '未知'
    },

    // 获取处理人信息
    getHandlerInfo (node) {
      switch (node.handler_type) {
        case 1:
          return this.getRoleNames(node.handler_role_ids) || '指定角色'
        case 2:
          return this.getUserNames(node.handler_user_ids) || '指定人员'
        case 3:
          return '发起人'
        case 4:
          return '上级领导'
        case 5:
          return '项目经理'
        case 6:
          return '客户'
        case 7:
          return '供应商'
        case 8:
          return '动态指定'
        default:
          return '未指定'
      }
    },

    // 获取角色名称
    getRoleNames (roleIds) {
      if (!roleIds) return '未指定'

      // 如果是数组，直接使用
      if (Array.isArray(roleIds)) {
        return roleIds.length > 0 ? `${roleIds.length}个角色` : '未指定'
      }

      // 如果是字符串，尝试解析为数组
      if (typeof roleIds === 'string') {
        try {
          const ids = JSON.parse(roleIds)
          return ids.length > 0 ? `${ids.length}个角色` : '未指定'
        } catch (e) {
          console.error('解析角色ID失败:', e)
          return '未指定'
        }
      }

      return '未指定'
    },

    // 获取用户名称
    getUserNames (userIds) {
      if (!userIds) return '未指定'

      // 如果是数组，直接使用
      if (Array.isArray(userIds)) {
        return userIds.length > 0 ? `${userIds.length}个人员` : '未指定'
      }

      // 如果是字符串，尝试解析为数组
      if (typeof userIds === 'string') {
        try {
          const ids = JSON.parse(userIds)
          return ids.length > 0 ? `${ids.length}个人员` : '未指定'
        } catch (e) {
          console.error('解析用户ID失败:', e)
          return '未指定'
        }
      }

      return '未指定'
    },

    // 检查是否有特殊要求
    hasRequirements (node) {
      return node.require_signature ||
             node.require_photos ||
             node.require_attachments ||
             node.require_payment ||
             node.require_quotation ||
             node.require_warranty_check
    },

    // 获取允许的操作列表
    getAllowedActions (actionsJson) {
      if (!actionsJson || actionsJson === '[]') return []
      try {
        const actions = JSON.parse(actionsJson)
        const actionMap = {
          agree: '同意',
          reject: '拒绝',
          transfer: '转交',
          withdraw: '撤回',
          skip: '跳过',
          addAttachment: '添加附件',
          addComment: '添加意见'
        }
        return actions.map(action => actionMap[action] || action)
      } catch {
        return []
      }
    },

    // 获取下一节点名称
    getNextNodeName (nodeId) {
      const node = this.nodes.find(n => n.id === nodeId)
      return node ? node.node_name : '未知节点'
    },

    // 格式化分支条件
    formatBranchConditions (conditionsJson) {
      if (!conditionsJson || conditionsJson === 'null') return '无分支条件'
      try {
        const conditions = JSON.parse(conditionsJson)
        return JSON.stringify(conditions, null, 2)
      } catch {
        return '格式错误'
      }
    },

    // 节点悬停
    handleNodeHover (node) {
      this.highlightedNode = node.id

      // 高亮相关连线
      this.connections.forEach((conn, index) => {
        if (conn.from === node.id || conn.to === node.id) {
          this.highlightedConnection = index
        }
      })
    },

    // 节点离开
    handleNodeLeave () {
      this.highlightedNode = null
      this.highlightedConnection = null
    },

    // 节点点击
    handleNodeClick (node) {
      this.selectedNode = node
    },

    // 放大
    handleZoomIn () {
      this.zoomLevel = Math.min(this.zoomLevel + 0.1, 2)
      this.zoomPercentage = Math.round(this.zoomLevel * 100)
    },

    // 缩小
    handleZoomOut () {
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.3)
      this.zoomPercentage = Math.round(this.zoomLevel * 100)
    },

    // 重置缩放
    handleReset () {
      this.zoomLevel = 0.8
      this.zoomPercentage = 80
      this.centerView()
    },

    // 滑块控制缩放
    handleZoomSlider (value) {
      this.zoomLevel = value / 100
    },

    // 居中显示
    centerView () {
      this.$nextTick(() => {
        const canvas = this.$refs.canvasRef
        if (canvas) {
          canvas.scrollLeft = (canvas.scrollWidth - canvas.clientWidth) / 2
          canvas.scrollTop = (canvas.scrollHeight - canvas.clientHeight) / 2
        }
      })
    },

    // 自动布局节点，仅在节点没有位置信息时使用
    autoLayoutNodes () {
      if (!this.nodes || this.nodes.length === 0) return

      // 检查是否所有节点都有位置信息
      const hasPositionInfo = this.nodes.every(node =>
        node.position_x !== undefined &&
        node.position_y !== undefined &&
        node.position_x !== null &&
        node.position_y !== null
      )

      // 如果所有节点都有位置信息，则不进行自动布局
      if (hasPositionInfo) {
        return
      }

      // 节点尺寸和间距
      const nodeWidth = 220
      const nodeHeight = 120
      const horizontalSpacing = 50
      const verticalSpacing = 80

      // 按节点类型分组
      const startNodes = this.nodes.filter(n => n.node_type === 1)
      const endNodes = this.nodes.filter(n => n.node_type === 6 || n.node_type === 4)
      const processNodes = this.nodes.filter(n => n.node_type !== 1 && n.node_type !== 6 && n.node_type !== 4)

      // 布局开始节点
      startNodes.forEach((node, index) => {
        node.position_x = 50
        node.position_y = 100 + index * (nodeHeight + verticalSpacing)
      })

      // 布局处理节点
      processNodes.forEach((node, index) => {
        const column = index % 3 + 1 // 最多3列
        const row = Math.floor(index / 3)

        node.position_x = 50 + column * (nodeWidth + horizontalSpacing)
        node.position_y = 100 + row * (nodeHeight + verticalSpacing)
      })

      // 布局结束节点
      endNodes.forEach((node, index) => {
        node.position_x = 50 + 4 * (nodeWidth + horizontalSpacing)
        node.position_y = 100 + index * (nodeHeight + verticalSpacing)
      })

      // 更新连线
      this.$nextTick(() => {
        this.generateConnections()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.process-preview-container {
  height: 80vh;
  display: flex;
  flex-direction: column;
  background: #fff;

  .preview-header {
    padding: 20px;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    .process-info {
      flex: 1;

      h3 {
        margin: 0 0 10px 0;
        font-size: 18px;
        color: #303133;
      }

      .info-tags {
        display: flex;
        gap: 8px;
        margin-bottom: 10px;
        flex-wrap: wrap;
      }

      .description {
        margin: 10px 0 0 0;
        color: #606266;
        font-size: 14px;
        line-height: 1.5;
      }
    }

    .preview-tools {
      display: flex;
      gap: 10px;
    }
  }

  .preview-stats {
    padding: 15px 20px;
    background: #f5f7fa;
    border-bottom: 1px solid #e4e7ed;

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(6, 1fr);
      gap: 15px;

      .stat-item {
        text-align: center;

        .stat-value {
          font-size: 24px;
          font-weight: 600;
          color: #409eff;
          margin-bottom: 5px;
        }

        .stat-label {
          font-size: 12px;
          color: #909399;
        }
      }
    }
  }

  .preview-canvas-container {
    flex: 1;
    position: relative;
    overflow: auto;
    background: #fafafa;
    min-height: 600px;

    .preview-canvas {
      position: relative;
      min-width: 100%;
      min-height: 100%;
      padding: 40px;
      transition: transform 0.3s ease;

      .canvas-grid {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-image:
          linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
          linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
        background-size: 40px 40px;
      }

      .nodes-container {
        position: relative;
        z-index: 10;
      }

      .connections-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 5;

        .connection-line {
          fill: none;
          stroke-width: 2;
          stroke: #409eff;
          transition: stroke 0.3s ease;

          &.line-highlight {
            stroke: #f56c6c;
            stroke-width: 3;
          }

          &.line-dashed {
            stroke-dasharray: 5,5;
          }

          &.line-condition {
            stroke: #67c23a;
          }

          &.line-parallel {
            stroke: #e6a23c;
          }
        }
      }
    }

    .zoom-controls {
      position: absolute;
      right: 20px;
      bottom: 20px;
      background: #fff;
      padding: 10px;
      border-radius: 4px;
      box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
      z-index: 200; // 确保在侧边栏之上
    }
  }

  .preview-sidebar {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 320px;
    background: #fff;
    border-left: 1px solid #e4e7ed;
    box-shadow: -2px 0 12px 0 rgba(0, 0, 0, 0.1);
    z-index: 100;
    overflow-y: auto;

    .sidebar-header {
      padding: 15px;
      border-bottom: 1px solid #e4e7ed;
      display: flex;
      justify-content: space-between;
      align-items: center;

      h4 {
        margin: 0;
        font-size: 16px;
        color: #303133;
      }
    }

    .node-detail {
      padding: 20px;

      .detail-section {
        margin-bottom: 20px;

        h5 {
          margin: 0 0 10px 0;
          font-size: 14px;
          color: #303133;
          font-weight: 500;
          border-left: 3px solid #409eff;
          padding-left: 10px;
        }

        .detail-row {
          display: flex;
          margin-bottom: 8px;
          font-size: 13px;

          .label {
            width: 80px;
            color: #909399;
            flex-shrink: 0;
          }

          .value {
            color: #606266;
            flex: 1;
            word-break: break-all;
          }
        }

        .requirements-list {
          .requirement-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 10px;
            margin-bottom: 5px;
            background: #f5f7fa;
            border-radius: 4px;
            font-size: 12px;
            color: #606266;

            i {
              color: #409eff;
            }
          }
        }

        .actions-list {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;

          .action-tag {
            margin: 2px;
          }
        }

        .branch-conditions {
          pre {
            margin: 0;
            padding: 10px;
            background: #f5f7fa;
            border-radius: 4px;
            font-size: 12px;
            color: #606266;
            max-height: 200px;
            overflow: auto;
          }
        }

        .remark-content {
          padding: 10px;
          background: #f5f7fa;
          border-radius: 4px;
          font-size: 13px;
          color: #606266;
          line-height: 1.5;
        }
      }
    }
  }

  .preview-legend {
    position: absolute;
    left: 20px;
    bottom: 20px;
    background: #fff;
    padding: 15px;
    border-radius: 4px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    z-index: 50;

    .legend-title {
      font-size: 14px;
      font-weight: 500;
      color: #303133;
      margin-bottom: 10px;
    }

    .legend-items {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        color: #606266;

        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 4px;
          border: 1px solid rgba(0, 0, 0, 0.1);

          &.node-type-1 { background: #67c23a; }
          &.node-type-2 { background: #409eff; }
          &.node-type-3 { background: #e6a23c; }
          &.node-type-4 { background: #f56c6c; }
          &.node-type-5 { background: #9b8bba; }
          &.node-type-6 { background: #909399; }
          &.node-type-7 { background: #8cc269; }
          &.node-type-8 { background: #f78989; }
          &.node-current {
            background: repeating-linear-gradient(
              45deg,
              #f56c6c,
              #f56c6c 10px,
              #fff 10px,
              #fff 20px
            );
          }
        }
      }
    }
  }

  .preview-node {
    position: absolute;
    min-height: 80px;
    background: #fff;
    border: 2px solid #dcdfe6;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 20;

    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
      transform: translateY(-2px);
    }

    &.node-highlight {
      border-color: #f56c6c;
      box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.1);
    }

    &.node-current {
      border-color: #f56c6c;
      border-style: double;
      animation: pulse 2s infinite;
    }

    &.node-start {
      border-color: #67c23a;
    }

    &.node-end {
      border-color: #909399;
    }

    &.node-type-1 { border-color: #67c23a; }
    &.node-type-2 { border-color: #409eff; }
    &.node-type-3 { border-color: #e6a23c; }
    &.node-type-4 { border-color: #f56c6c; }
    &.node-type-5 { border-color: #9b8bba; }
    &.node-type-6 { border-color: #909399; }
    &.node-type-7 { border-color: #8cc269; }
    &.node-type-8 { border-color: #f78989; }

    .node-header {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      background: #f5f7fa;
      border-bottom: 1px solid #e4e7ed;
      border-radius: 6px 6px 0 0;

      .node-icon {
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 10px;
        flex-shrink: 0;

        i {
          font-size: 14px;
          color: #409eff;
        }
      }

      .node-title {
        flex: 1;
        min-width: 0;

        .node-name {
          font-weight: 500;
          color: #303133;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .node-no {
          font-size: 11px;
          color: #909399;
          margin-top: 2px;
        }
      }
    }

    .node-content {
      padding: 12px;

      .node-handler {
        font-size: 12px;
        margin-bottom: 8px;

        .handler-label {
          color: #909399;
        }

        .handler-value {
          color: #606266;
          font-weight: 500;
        }
      }

      .node-requirements {
        margin-top: 10px;
        padding-top: 10px;
        border-top: 1px dashed #e4e7ed;

        .requirement-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          color: #606266;
          margin-bottom: 4px;

          i {
            color: #409eff;
            font-size: 10px;
          }
        }
      }

      .node-time {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 11px;
        color: #e6a23c;
        margin-top: 8px;

        i {
          font-size: 12px;
        }
      }
    }

    .node-footer {
      padding: 8px 12px;
      background: #fafafa;
      border-top: 1px solid #e4e7ed;
      border-radius: 0 0 6px 6px;

      .node-status {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .node-type-text {
          font-size: 11px;
          color: #909399;
        }
      }
    }
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(245, 108, 108, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(245, 108, 108, 0);
  }
}
</style>
